/**
 * Typography Design Tokens
 * Based on Figma Design System - Vasit Admin
 * Font Family: Outfit
 */

// Font Family
export const fontFamily = {
  // Keep AntD typography in sync with CSS variables (and allow Cyrillic-safe primary font)
  primary: 'var(--font-family-primary)',
} as const;

// Font Weights
export const fontWeight = {
  regular: 400,
  medium: 500,
  semibold: 600,
  bold: 700,
} as const;

// Font Sizes (in px and rem)
export const fontSize = {
  // Display sizes
  display2xl: 72, // 4.5rem
  displayXl: 60, // 3.75rem
  displayLg: 48, // 3rem
  displayMd: 36, // 2.25rem
  displaySm: 30, // 1.875rem
  displayXs: 24, // 1.5rem
  // Text sizes
  textXl: 20, // 1.25rem
  textLg: 18, // 1.125rem
  textMd: 16, // 1rem (base)
  textSm: 14, // 0.875rem
  textXs: 12, // 0.75rem
} as const;

// Line Heights (in px)
export const lineHeight = {
  // Display line heights
  display2xl: 90, // 5.625rem
  displayXl: 72, // 4.5rem
  displayLg: 60, // 3.75rem
  displayMd: 44, // 2.75rem
  displaySm: 38, // 2.375rem
  displayXs: 32, // 2rem
  // Text line heights
  textXl: 30, // 1.875rem
  textLg: 28, // 1.75rem
  textMd: 24, // 1.5rem
  textSm: 20, // 1.25rem
  textXs: 18, // 1.125rem
} as const;

// Typography Presets (ready-to-use styles)
export const typography = {
  // Display 2xl variants
  display2xlRegular: {
    fontFamily: fontFamily.primary,
    fontSize: fontSize.display2xl,
    lineHeight: `${lineHeight.display2xl}px`,
    fontWeight: fontWeight.regular,
  },
  display2xlMedium: {
    fontFamily: fontFamily.primary,
    fontSize: fontSize.display2xl,
    lineHeight: `${lineHeight.display2xl}px`,
    fontWeight: fontWeight.medium,
  },
  display2xlSemibold: {
    fontFamily: fontFamily.primary,
    fontSize: fontSize.display2xl,
    lineHeight: `${lineHeight.display2xl}px`,
    fontWeight: fontWeight.semibold,
  },
  display2xlBold: {
    fontFamily: fontFamily.primary,
    fontSize: fontSize.display2xl,
    lineHeight: `${lineHeight.display2xl}px`,
    fontWeight: fontWeight.bold,
  },

  // Display xl variants
  displayXlRegular: {
    fontFamily: fontFamily.primary,
    fontSize: fontSize.displayXl,
    lineHeight: `${lineHeight.displayXl}px`,
    fontWeight: fontWeight.regular,
  },
  displayXlMedium: {
    fontFamily: fontFamily.primary,
    fontSize: fontSize.displayXl,
    lineHeight: `${lineHeight.displayXl}px`,
    fontWeight: fontWeight.medium,
  },
  displayXlSemibold: {
    fontFamily: fontFamily.primary,
    fontSize: fontSize.displayXl,
    lineHeight: `${lineHeight.displayXl}px`,
    fontWeight: fontWeight.semibold,
  },
  displayXlBold: {
    fontFamily: fontFamily.primary,
    fontSize: fontSize.displayXl,
    lineHeight: `${lineHeight.displayXl}px`,
    fontWeight: fontWeight.bold,
  },

  // Display lg variants
  displayLgRegular: {
    fontFamily: fontFamily.primary,
    fontSize: fontSize.displayLg,
    lineHeight: `${lineHeight.displayLg}px`,
    fontWeight: fontWeight.regular,
  },
  displayLgMedium: {
    fontFamily: fontFamily.primary,
    fontSize: fontSize.displayLg,
    lineHeight: `${lineHeight.displayLg}px`,
    fontWeight: fontWeight.medium,
  },
  displayLgSemibold: {
    fontFamily: fontFamily.primary,
    fontSize: fontSize.displayLg,
    lineHeight: `${lineHeight.displayLg}px`,
    fontWeight: fontWeight.semibold,
  },
  displayLgBold: {
    fontFamily: fontFamily.primary,
    fontSize: fontSize.displayLg,
    lineHeight: `${lineHeight.displayLg}px`,
    fontWeight: fontWeight.bold,
  },

  // Display md variants
  displayMdRegular: {
    fontFamily: fontFamily.primary,
    fontSize: fontSize.displayMd,
    lineHeight: `${lineHeight.displayMd}px`,
    fontWeight: fontWeight.regular,
  },
  displayMdMedium: {
    fontFamily: fontFamily.primary,
    fontSize: fontSize.displayMd,
    lineHeight: `${lineHeight.displayMd}px`,
    fontWeight: fontWeight.medium,
  },
  displayMdSemibold: {
    fontFamily: fontFamily.primary,
    fontSize: fontSize.displayMd,
    lineHeight: `${lineHeight.displayMd}px`,
    fontWeight: fontWeight.semibold,
  },
  displayMdBold: {
    fontFamily: fontFamily.primary,
    fontSize: fontSize.displayMd,
    lineHeight: `${lineHeight.displayMd}px`,
    fontWeight: fontWeight.bold,
  },

  // Display sm variants
  displaySmRegular: {
    fontFamily: fontFamily.primary,
    fontSize: fontSize.displaySm,
    lineHeight: `${lineHeight.displaySm}px`,
    fontWeight: fontWeight.regular,
  },
  displaySmMedium: {
    fontFamily: fontFamily.primary,
    fontSize: fontSize.displaySm,
    lineHeight: `${lineHeight.displaySm}px`,
    fontWeight: fontWeight.medium,
  },
  displaySmSemibold: {
    fontFamily: fontFamily.primary,
    fontSize: fontSize.displaySm,
    lineHeight: `${lineHeight.displaySm}px`,
    fontWeight: fontWeight.semibold,
  },
  displaySmBold: {
    fontFamily: fontFamily.primary,
    fontSize: fontSize.displaySm,
    lineHeight: `${lineHeight.displaySm}px`,
    fontWeight: fontWeight.bold,
  },

  // Display xs variants
  displayXsRegular: {
    fontFamily: fontFamily.primary,
    fontSize: fontSize.displayXs,
    lineHeight: `${lineHeight.displayXs}px`,
    fontWeight: fontWeight.regular,
  },
  displayXsMedium: {
    fontFamily: fontFamily.primary,
    fontSize: fontSize.displayXs,
    lineHeight: `${lineHeight.displayXs}px`,
    fontWeight: fontWeight.medium,
  },
  displayXsSemibold: {
    fontFamily: fontFamily.primary,
    fontSize: fontSize.displayXs,
    lineHeight: `${lineHeight.displayXs}px`,
    fontWeight: fontWeight.semibold,
  },
  displayXsBold: {
    fontFamily: fontFamily.primary,
    fontSize: fontSize.displayXs,
    lineHeight: `${lineHeight.displayXs}px`,
    fontWeight: fontWeight.bold,
  },

  // Text xl variants
  textXlRegular: {
    fontFamily: fontFamily.primary,
    fontSize: fontSize.textXl,
    lineHeight: `${lineHeight.textXl}px`,
    fontWeight: fontWeight.regular,
  },
  textXlMedium: {
    fontFamily: fontFamily.primary,
    fontSize: fontSize.textXl,
    lineHeight: `${lineHeight.textXl}px`,
    fontWeight: fontWeight.medium,
  },
  textXlSemibold: {
    fontFamily: fontFamily.primary,
    fontSize: fontSize.textXl,
    lineHeight: `${lineHeight.textXl}px`,
    fontWeight: fontWeight.semibold,
  },
  textXlBold: {
    fontFamily: fontFamily.primary,
    fontSize: fontSize.textXl,
    lineHeight: `${lineHeight.textXl}px`,
    fontWeight: fontWeight.bold,
  },

  // Text lg variants
  textLgRegular: {
    fontFamily: fontFamily.primary,
    fontSize: fontSize.textLg,
    lineHeight: `${lineHeight.textLg}px`,
    fontWeight: fontWeight.regular,
  },
  textLgMedium: {
    fontFamily: fontFamily.primary,
    fontSize: fontSize.textLg,
    lineHeight: `${lineHeight.textLg}px`,
    fontWeight: fontWeight.medium,
  },
  textLgSemibold: {
    fontFamily: fontFamily.primary,
    fontSize: fontSize.textLg,
    lineHeight: `${lineHeight.textLg}px`,
    fontWeight: fontWeight.semibold,
  },
  textLgBold: {
    fontFamily: fontFamily.primary,
    fontSize: fontSize.textLg,
    lineHeight: `${lineHeight.textLg}px`,
    fontWeight: fontWeight.bold,
  },

  // Text md variants (base)
  textMdRegular: {
    fontFamily: fontFamily.primary,
    fontSize: fontSize.textMd,
    lineHeight: `${lineHeight.textMd}px`,
    fontWeight: fontWeight.regular,
  },
  textMdMedium: {
    fontFamily: fontFamily.primary,
    fontSize: fontSize.textMd,
    lineHeight: `${lineHeight.textMd}px`,
    fontWeight: fontWeight.medium,
  },
  textMdSemibold: {
    fontFamily: fontFamily.primary,
    fontSize: fontSize.textMd,
    lineHeight: `${lineHeight.textMd}px`,
    fontWeight: fontWeight.semibold,
  },
  textMdBold: {
    fontFamily: fontFamily.primary,
    fontSize: fontSize.textMd,
    lineHeight: `${lineHeight.textMd}px`,
    fontWeight: fontWeight.bold,
  },

  // Text sm variants
  textSmRegular: {
    fontFamily: fontFamily.primary,
    fontSize: fontSize.textSm,
    lineHeight: `${lineHeight.textSm}px`,
    fontWeight: fontWeight.regular,
  },
  textSmMedium: {
    fontFamily: fontFamily.primary,
    fontSize: fontSize.textSm,
    lineHeight: `${lineHeight.textSm}px`,
    fontWeight: fontWeight.medium,
  },
  textSmSemibold: {
    fontFamily: fontFamily.primary,
    fontSize: fontSize.textSm,
    lineHeight: `${lineHeight.textSm}px`,
    fontWeight: fontWeight.semibold,
  },
  textSmBold: {
    fontFamily: fontFamily.primary,
    fontSize: fontSize.textSm,
    lineHeight: `${lineHeight.textSm}px`,
    fontWeight: fontWeight.bold,
  },

  // Text xs variants
  textXsRegular: {
    fontFamily: fontFamily.primary,
    fontSize: fontSize.textXs,
    lineHeight: `${lineHeight.textXs}px`,
    fontWeight: fontWeight.regular,
  },
  textXsMedium: {
    fontFamily: fontFamily.primary,
    fontSize: fontSize.textXs,
    lineHeight: `${lineHeight.textXs}px`,
    fontWeight: fontWeight.medium,
  },
  textXsSemibold: {
    fontFamily: fontFamily.primary,
    fontSize: fontSize.textXs,
    lineHeight: `${lineHeight.textXs}px`,
    fontWeight: fontWeight.semibold,
  },
  textXsBold: {
    fontFamily: fontFamily.primary,
    fontSize: fontSize.textXs,
    lineHeight: `${lineHeight.textXs}px`,
    fontWeight: fontWeight.bold,
  },
} as const;

// Ant Design Theme Token Override
export const antdTypographyToken = {
  fontFamily: fontFamily.primary,
  fontSize: fontSize.textMd,
  fontSizeSM: fontSize.textSm,
  fontSizeLG: fontSize.textLg,
  fontSizeXL: fontSize.textXl,
  fontSizeHeading1: fontSize.displayLg,
  fontSizeHeading2: fontSize.displayMd,
  fontSizeHeading3: fontSize.displaySm,
  fontSizeHeading4: fontSize.displayXs,
  fontSizeHeading5: fontSize.textXl,
  lineHeight: lineHeight.textMd / fontSize.textMd,
  lineHeightSM: lineHeight.textSm / fontSize.textSm,
  lineHeightLG: lineHeight.textLg / fontSize.textLg,
  lineHeightHeading1: lineHeight.displayLg / fontSize.displayLg,
  lineHeightHeading2: lineHeight.displayMd / fontSize.displayMd,
  lineHeightHeading3: lineHeight.displaySm / fontSize.displaySm,
  lineHeightHeading4: lineHeight.displayXs / fontSize.displayXs,
  lineHeightHeading5: lineHeight.textXl / fontSize.textXl,
  fontWeightStrong: fontWeight.semibold,
} as const;
